<template>
  <div class="klasha-page">
    <div class="klasha-page__wrap">
      <div v-if="templateData.logo" class="klasha-page__logo">
        <img :src="templateData.logo" alt="" />
      </div>
      <div class="klasha-page__id">{{ $t('common__invoice') }} {{ templateData.reference_id }}</div>
      <div v-if="templateData.description" class="klasha-page__descriptions">
        {{ templateData.description }}
      </div>
      <div class="klasha-page__duration">
        {{ $t('invoice_flow__multi_time_remainin') }}:
        <span :class="formatedTime.class" class="klasha-page__duration-time">
          {{ formatedTime.time }}
        </span>
      </div>
      <div class="klasha-page__info">
        <div class="klasha-page__info-label">
          {{ $t('common__amount_to_pay') }}
        </div>
        <div class="klasha-page__info-value">
          <span v-if="templateData.symbol" class="klasha-page__info-value__sybmol">
            {{ templateData.symbol }}
          </span>
          <div class="klasha-page__info-value__amount">
            <div class="klasha-page__info-value__amount-first">
              {{ templateData.amount.firstPart }}
            </div>
            <div v-if="templateData.amount.lastPart" class="klasha-page__info-value__amount-last">
              {{ templateData.amount.lastPart }}
            </div>
          </div>
          <span v-if="!templateData.symbol" class="klasha-page__info-value__currency">
            {{ templateData.currency }}
          </span>
          <ButtonCopy :value="templateData.amount.full.toString()" is-gray />
        </div>
      </div>
      <template v-if="templateData.barcode">
        <div class="klasha-page__barcode">
          <svg ref="barcodeSvg"></svg>

          <div class="klasha-page__barcode-link">
            <ButtonCopy
              :value="templateData.barcode"
              has-border
              class="klasha-page__barcode-btn"
              is-gray>
              <div>{{ $t('invoice_flow__multi_copy_code') }}</div>
            </ButtonCopy>
          </div>
        </div>
      </template>

      <template v-else-if="templateData.qr_code">
        <div class="klasha-page__qr">
          <QrcodeVue :value="templateData.qr_code" level="M" size="180" />
        </div>

        <div class="klasha-page__alt">
          {{ $t('invoice_flow__multi_or') }}
        </div>

        <a
          v-if="isMobile && templateData.deep_link"
          :href="templateData.deep_link"
          class="klasha-page__deep-link"
          target="_blank">
          <img src="./assets/app.svg" alt="" class="klasha-page__deep-link__img" />
          <span class="klasha-page__deep-link__text">
            {{ $t('invoice_flow__multi_open_app') }}
          </span>
        </a>

        <div v-else class="klasha-page__qr-link">
          <ElInput :value="templateData.qr_code" readonly />
          <ButtonCopy :value="templateData.qr_code" class="klasha-page__qr-link-btn" is-gray />
        </div>
      </template>

      <div v-for="(item, key) in fields" :key="key" class="klasha-page__field">
        <Field :value="item" :label="$t(`field__${key}`)" readonly>
          <template #prefix>
            <ButtonCopy :value="item" is-gray />
          </template>
        </Field>
      </div>

      <div v-for="(step, index) in steps" :key="index" class="klasha-page__step">
        <div class="klasha-page__step-number">
          {{ index + 1 }}
        </div>
        <div v-html="sanitizeText(step)" class="klasha-page__step-label" />
      </div>

      <ConfirmationForm
        v-if="confirmationPayload && !!Object.keys(confirmationPayload).length"
        ref="formFields"
        :fields="confirmationPayload"
        :method="paymentInvoice.service_method" />

      <button :disabled="loading" class="klasha-page__button" @click="confirm">
        <div v-if="loading" class="klasha-page__button-loader">
          <Loader />
        </div>
        {{ $t('klasha__button') }}
      </button>
    </div>
    <div :class="{ 'no-btn': !templateData.has_return_url }" class="klasha-page__footer">
      <div
        v-if="templateData.has_return_url"
        class="klasha-page__footer-back"
        @click="redirectReturnUrl(true)">
        <img src="./assets/arrow.svg" alt="" />

        <span>
          {{ $t('invoice_flow__multi_return_button') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ButtonCopy from './components/ButtonCopy.vue';
import Loader from './components/Loader.vue';
import { createDayJsDurationFormatterFactory } from '@/helpers/DayJsDurationFormatter/DayJsDurationFormatterImpl';
import QrcodeVue from 'qrcode.vue';
import Field from './components/Field.vue';
import ConfirmationForm from './components/Form.vue';
import { sanitizeHtmlOptions } from '@/helpers/sanitizeHtmlOptions';
import sanitizeHtml from 'sanitize-html';
import messages from './translations/index';
import JsBarcode from 'jsbarcode';
import ElInput from 'element-ui/lib/input';

const min30 = 1800000;
const min15 = 900000;

export default {
  components: { ButtonCopy, ElInput, QrcodeVue, Loader, Field, ConfirmationForm },

  created() {
    this.selectLocale('en');
    this.startTimer(this.templateData.expires);
  },

  mounted() {
    if (this.templateData.barcode) {
      this.generateBarcode(this.templateData.barcode);
    }
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData']),
    ...mapState('InvoiceLifetime', ['isExpired', 'timeToExpired']),

    confirmationPayload() {
      const { payload } = (this.templateData || {}).confirmation || {};
      return typeof payload === 'string' ? JSON.parse(payload) : payload;
    },

    currentLocale() {
      return this.$i18n.locale;
    },

    fields() {
      const { requisites } = this.flowData.payload;
      let data = {
        account_number: null,
        bank_code: null,
        bank_ifsc_code: null,
        account_holder: null,
        account_holder_address: null,
        bank_name: null,
        bank_address: null,
        bic: null,
        purpose_of_payment: null,
      };

      if (requisites) {
        const objRequisites = typeof requisites === 'string' ? JSON.parse(requisites) : requisites;
        Object.keys(data).forEach(key => {
          if (key in objRequisites && !['qr_code_data', 'deep_link', 'barcode'].includes(key)) {
            data[key] = objRequisites[key];
          }
        });
      }

      Object.keys(data).forEach(key => {
        if (!data[key]) delete data[key];
      });
      return data;
    },

    steps() {
      return Array(5)
        .fill('', 0, 5)
        .map((_, index) => {
          const translationKey = `klasha__${this.templateData.is_otp_flow ? 'otp_' : ''}step_${
            index + 1
          }`;

          return index === 0
            ? this.$t(translationKey, { phone: this.templateData.customer_phone })
            : this.$t(translationKey);
        });
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    dateFormatter() {
      return createDayJsDurationFormatterFactory({
        defaultTemplate: 'HH:mm:ss',
      });
    },

    formatedTime() {
      const time = this.dateFormatter().format(this.timeToExpired);

      const classTime =
        this.timeToExpired > min30
          ? null
          : this.timeToExpired <= min30 && this.timeToExpired > min15
          ? 'medium'
          : 'low';

      return {
        time,
        class: classTime,
      };
    },

    templateData() {
      const { reference_id, description, has_return_url, id, expires } = this.paymentInvoice;

      const { amount: mainAmount, currency: mainCurrency } = this.flowData;

      const {
        confirmation,
        method,
        requisites,
        amount: payloadAmount,
        currency: payloadCurrency,
        is_otp_flow,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) {
          return {};
        }

        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch (e) {
            return {};
          }
        }

        return requisites;
      };

      const { deep_link, qr_code_data, barcode, customer_phone } = parseRequisites();

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;

      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      return {
        id,
        reference_id,
        description,
        amount: this.parseAmount(amount),
        symbol: this.currencySymbol(currency),
        currency,
        logo: (method || {}).logo || null,
        expires,
        has_return_url,
        confirmation,
        qr_code: deep_link || qr_code_data || '',
        deep_link,
        barcode,
        is_otp_flow,
        customer_phone,
      };
    },
  },

  methods: {
    ...mapActions(['confirmPaymentInvoice']),
    ...mapActions('InvoiceLifetime', ['startTimer']),

    generateBarcode(value) {
      if (this.$refs.barcodeSvg) {
        JsBarcode(this.$refs.barcodeSvg, value, {
          format: 'CODE128',
          lineColor: '#000',
          width: 2,
          height: 100,
          displayValue: true,
        });
      }
    },

    checkLocaleIsSupportInPage(locale) {
      return !!Object.keys(messages).some(key => key === locale);
    },

    sanitizeText(val) {
      return sanitizeHtml(val, {
        ...sanitizeHtmlOptions,
      });
    },

    async confirm() {
      let data = {};
      const { id } = this.paymentInvoice;

      if (
        !(this.templateData.confirmation && Object.keys(this.templateData.confirmation).length) ||
        !this.templateData.confirmation.required
      ) {
        this.redirectReturnUrl();

        return;
      }

      if (this.confirmationPayload && !!Object.keys(this.confirmationPayload).length) {
        const resp = await this.$refs.formFields.submit();

        if (resp.valid) {
          data = { payload: { ...resp.form } };
        } else {
          return;
        }
      }

      if (!this.loading) {
        this.loading = true;
        const { status } = await this.confirmPaymentInvoice({ id: this.templateData.id, data });

        if (status === 200) {
          this.redirectReturnUrl();
        } else {
          this.loading = false;
        }
      }
    },

    selectLocale(locale) {
      this.$bus.$emit('changeLocale', locale);
    },

    redirectReturnUrl(force) {
      if (!this.templateData.has_return_url) {
        return;
      }

      const isForce = force ? '&force=true' : '';
      location.replace(`/return?id=${this.paymentInvoice.id}${isForce}`);
    },

    currencySymbol(currency) {
      switch (currency) {
        case 'UAH':
          return '₴';
        case 'USD':
          return '$';
        case 'EUR':
          return '€';
        case 'RUB':
          return '₽';
        default:
          return null;
      }
    },

    parseAmount(amount) {
      const [firstPart, lastPart] = amount.toString().split('.');

      return {
        full: amount,
        firstPart,
        lastPart: parseInt(lastPart) > 0 ? `.${lastPart}` : null,
      };
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
$--blue-color: #334dff;
$--white-color: #ffffff;

$--label-color-1: #7f8fa4;
$--label-color-2: #334150;
$--card-label-color: #b3bdff;
$--card-value-color: $--white-color;

$--step-bg-color: #f1f3ff;
$--step-label-color-1: $--blue-color;

$--button-bg-color: #0020ff;
$--button-hover-bg-color: $--blue-color;
$--button-active-bg-color: #001acc;
$--button-disabled-bg-color: #d6daf5;

@media (max-width: 480px) {
  canvas {
    width: 120px !important;
    height: 120px !important;
  }
}

.klasha-page {
  display: flex;
  align-items: center;
  flex-direction: column;
  font-family: 'Roboto', sans-serif;

  &__logo {
    margin-bottom: 16px;
    img {
      height: 24px;
    }

    @media (max-width: 480px) {
      margin-bottom: 12px;
    }
  }

  &__alt {
    padding: 4px 0;
    margin-bottom: 16px;
    position: relative;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #cdd5db;

    &::before,
    &::after {
      content: '';
      position: absolute;
      height: 2px;
      background: #eaecee;
      width: calc(43% - 16px);
      top: calc(50% - 2px);
    }

    &::before {
      left: 0;
    }

    &::after {
      right: 0;
    }
  }

  &__qr-link {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    margin-bottom: 24px;
    position: relative;

    .el-input {
      width: 100%;
      margin: 0 60px;

      @media (max-width: 480px) {
        margin: 30px;
      }

      &__inner {
        width: 100%;
        padding: 10px 16px;
        border: 1px solid #0020ff;
        border-radius: 6px;
        color: #334150;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.5px;
        outline: unset;
        transition: border-color 0.3s;
      }
    }

    &-btn {
      position: absolute;
      right: 28px;

      @media (max-width: 480px) {
        right: 0px;
      }
    }
  }

  &__card {
    box-shadow: 0px 8px 24px -18px rgba(91, 105, 204, 0.32);
    border-radius: 12px;
    max-width: 340px;
    width: 100%;
    margin: 16px auto 32px;
    position: relative;
    overflow: hidden;

    &-bg {
      display: block;
      width: 100%;
      position: absolute;
      z-index: 0;
      top: 0;
      left: 0;
      height: 100%;
    }

    &-wrap {
      position: inherit;
      width: 100%;
      min-height: 192px;
      padding: 24px 24px 24px 20px;
      z-index: 1;
    }

    &-header,
    &-main,
    &-footer {
      display: flex;
      justify-content: space-between;
    }

    &-main {
      margin: 32px 0;

      &.max {
        margin: 48px 0 32px;

        @media (max-width: 380px) {
          margin: 32px 0 26px;
        }
      }

      @media (max-width: 380px) {
        margin: 26px 0;
      }

      &__button {
        display: flex;
        align-items: center;
      }

      .button-copy {
        @media (max-width: 380px) {
          width: 18px;
          height: 18px;

          svg {
            width: 8px;
          }
        }
      }

      &__number {
        display: flex;
        gap: 12px;
        color: $--card-value-color;
        letter-spacing: 2px;
        font-weight: 700;
        font-size: 20px;
        line-height: 24px;

        @media (max-width: 460px) {
          font-size: 16px;
          gap: 8px;
        }

        @media (max-width: 380px) {
          font-size: 14px;
          gap: 4px;
        }
      }
    }

    &-footer {
      &__col {
        &-label {
          font-weight: 500;
          font-size: 12px;
          line-height: 16px;
          color: $--card-label-color;
          margin-bottom: 4px;
          text-align: left;
          @media (max-width: 380px) {
            font-size: 10px;
          }
        }
        &-value {
          font-weight: 500;
          font-size: 14px;
          line-height: 16px;
          color: $--card-value-color;
          text-align: right;

          @media (max-width: 380px) {
            font-size: 12px;
          }
          &.holder {
            text-align: left;
          }
        }
      }
    }
  }

  &__deep-link {
    padding: 8px 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border: 1px solid $--blue-color;
    border-radius: 6px;
    width: fit-content;
    margin: 0 auto 24px;

    @media (max-width: 480px) {
      margin: 0 auto 16px;
    }

    &__img {
      height: 16px;
    }

    &__text {
      letter-spacing: 0.5px;
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
    }
  }

  &__qr {
    margin-bottom: 24px;
  }

  &__barcode {
    & > svg {
      max-width: 100%;
      height: 100px;
      object-fit: contain;
      margin-bottom: 20px;

      @media (max-width: 480px) {
        height: 80px;
        margin-bottom: 10px;
      }
    }

    &-link {
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: center;
      margin-bottom: 24px;
      position: relative;

      .el-input {
        width: 100%;
        margin: 0 60px;
        input {
          text-overflow: ellipsis;
        }

        @media (max-width: 480px) {
          margin: 0 30px;
        }

        &__inner {
          width: 100%;
          padding: 10px 16px;
          border: 1px solid #0020ff;
          border-radius: 6px;
          color: #334150;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.5px;
          outline: unset;
          transition: border-color 0.3s;
        }
      }
    }
    .button-copy {
      width: fit-content;
      padding: 15px 20px;
      gap: 5px;
    }
  }

  &__duration {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    color: $--label-color-2;
    margin-bottom: 24px;

    @media (max-width: 480px) {
      margin-bottom: 16px;
    }
    &-time {
      &.low {
        color: #e82a41;
      }

      &.medium {
        color: #ffbe17;
      }
    }
  }

  &__wrap {
    max-width: 540px;
    width: 100%;
    padding: 32px;
    box-shadow: 0px 50px 60px -30px rgba(50, 74, 101, 0.15);
    border-radius: 10px;
    background: #ffffff;
  }

  &__footer {
    max-width: 540px;
    width: 100%;
    margin: 16px 0;
    display: flex;
    justify-content: space-between;

    &.no-btn {
      justify-content: flex-end;
    }

    &-back {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.5px;
      color: $--button-bg-color;
      display: flex;
      gap: 9px;
      cursor: pointer;
      align-items: center;

      img {
        height: 6.63px;
      }
    }

    &-locale {
      display: flex;
      align-items: center;
      &__item {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        color: #7f8fa4;
        transition: color 0.3s;
        border-right: 1px solid #cdd5db;
        cursor: pointer;
        padding: 0 8px;

        &.active,
        &:hover {
          color: $--button-bg-color;
        }

        &:first-child {
          padding: 0 8px 0 9.5px;
        }

        &:last-child {
          border-right: unset;
          padding: 0 0 0 8px;
        }
      }
    }
  }

  &__title {
    letter-spacing: 0.5px;
    color: $--label-color-1;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 4px;
  }

  &__id {
    letter-spacing: 1px;
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: $--label-color-2;
    margin-bottom: 12px;
  }

  &__descriptions {
    text-align: center;
    letter-spacing: 0.5px;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: $--label-color-1;
    margin-bottom: 24px;

    @media (max-width: 480px) {
      margin-bottom: 16px;
    }
  }

  &__info {
    margin-bottom: 20px;

    @media (max-width: 480px) {
      margin-bottom: 16px;
    }

    &-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.5px;
      color: $--label-color-1;
      margin-bottom: 4px;
    }

    &-value {
      display: flex;
      justify-content: center;
      align-items: center;
      letter-spacing: 1px;
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      color: $--label-color-2;
      &__amount {
        margin: 0 8px;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;

        &-last {
          line-height: 27px;
          align-self: flex-end;
          font-size: 16px;
        }
      }
    }
  }

  &__step {
    display: grid;
    grid-template-columns: 20px auto;
    grid-gap: 8px;
    margin-top: 12px;

    &-number {
      font-weight: 500;
      font-size: 10px;
      line-height: 16px;
      letter-spacing: 0.5px;
      font-feature-settings: 'liga' off;
      background: #f1f3ff;
      color: $--blue-color;
      border-radius: 50%;
      width: 20px !important;
      height: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &-label {
      text-align: left;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: $--label-color-2;
    }
  }

  &__button {
    background: $--button-bg-color;
    border-radius: 6px;
    padding: 12px;
    width: 100%;
    max-width: 340px;
    margin: 32px auto 0;
    letter-spacing: 0.5px;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    color: $--white-color;
    cursor: pointer;
    transition: background 0.3s;
    position: relative;
    overflow: hidden;
    border: unset;

    @media (max-width: 480px) {
      margin: 24px auto 0;
    }

    &-loader {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #0000006e;
    }

    &:hover {
      background: $--button-hover-bg-color;
    }

    &:active {
      background: $--button-active-bg-color;
    }

    &:disabled {
      background: $--button-disabled-bg-color;
      cursor: not-allowed;
    }
  }

  &__message {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: $--label-color-1;
    margin: 16px auto;
    width: 100%;
    max-width: 340px;
  }
}
</style>
